import type { Account, AccountInfo } from "../DataModels";

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

export interface LocalDevIdentity {
  userId: string;
  accountId: string;
  appId: string;
  audience: string;
  email: string;
  playerName: string;
  guildName: string;
  guildTag: string;
  expiresAtUtcMillis: number;
  accessToken: string;
  refreshToken: string;
  accounts: AccountInfo[];
}

function isBrowserDev(): boolean {
  return process.env.NODE_ENV === "development";
}

const browserEnv = {
  userId: process.env.GATSBY_LOCAL_DEV_USER_ID,
  accountId: process.env.GATSBY_LOCAL_DEV_ACCOUNT_ID,
  appId: process.env.GATSBY_LOCAL_DEV_APP_ID,
  audience: process.env.GATSBY_LOCAL_DEV_AUD,
  email: process.env.GATSBY_LOCAL_DEV_EMAIL,
  playerName: process.env.GATSBY_LOCAL_DEV_PLAYER_NAME,
  guildName: process.env.GATSBY_LOCAL_DEV_GUILD_NAME,
  guildTag: process.env.GATSBY_LOCAL_DEV_GUILD_TAG,
};

function readEnv(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

function encodeBase64Url(value: string): string {
  const base64 =
    typeof btoa === "function"
      ? btoa(unescape(encodeURIComponent(value)))
      : Buffer.from(value, "utf8").toString("base64");

  return base64
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

function makeLocalDevStarToken(
  userId: string,
  accountId: string,
  email: string,
  audience: string,
  expiresAtUtcMillis: number
): string {
  const exp = Math.floor(expiresAtUtcMillis / 1000);
  return [
    encodeBase64Url(JSON.stringify({ alg: "none", typ: "JWT" })),
    encodeBase64Url(JSON.stringify({ sI: userId, aI: accountId, cT: `local(${email})`, aud: audience, exp })),
    "local-dev-signature",
  ].join(".");
}

export function getLocalDevIdentity(): LocalDevIdentity | null {
  if (!isBrowserDev()) return null;

  const userId = readEnv(browserEnv.userId);
  const accountId = readEnv(browserEnv.accountId);
  if (!userId || !accountId) return null;

  const appId = readEnv(browserEnv.appId) ?? "aircombat";
  const audience = readEnv(browserEnv.audience) ?? "local-dev";
  const email = readEnv(browserEnv.email) ?? "local-dev@starform.local";
  const playerName = readEnv(browserEnv.playerName) ?? "Local Dev";
  const guildName = readEnv(browserEnv.guildName) ?? "Dev Squadron";
  const guildTag = readEnv(browserEnv.guildTag) ?? "DEV";
  const expiresAtUtcMillis = Date.now() + ONE_YEAR_MS;
  const accessToken = makeLocalDevStarToken(userId, accountId, email, audience, expiresAtUtcMillis);

  return {
    userId,
    accountId,
    appId,
    audience,
    email,
    playerName,
    guildName,
    guildTag,
    expiresAtUtcMillis,
    accessToken,
    refreshToken: "local-dev-refresh",
    accounts: [
      {
        accountId,
        appId,
        playerInfo: {
          playerName,
          hardCurrency: 0,
          softCurrency: 0,
          loyaltyPoints: 0,
          guildName,
          guildTag,
        },
      },
    ],
  };
}

export function isLocalDevAuthEnabled(): boolean {
  return getLocalDevIdentity() !== null;
}

export function buildLocalDevAccountStats(identity: LocalDevIdentity): Record<string, Account> {
  const now = Date.now();
  const creationTime = new Date(now - 30 * 24 * 60 * 60 * 1000);
  const aircraftId = "aircraft:f5-v2";

  return {
    [identity.accountId]: {
      userDetails: {
        userId: identity.userId,
        firstCountry: "US",
        whitelistedById: null,
        redeemedRedemptionCodes: [],
        isBanned: false,
        loginCredentialsVersion: "local-dev",
        isDeveloper: true,
        isSilenced: false,
        systemLanguage: "en-US",
        emailUserRegistration: {
          rawEmailAddress: identity.email,
          registrationTimeUnix: Math.floor(creationTime.getTime() / 1000),
          registrationTime: creationTime,
        },
        accounts: [{ accountId: identity.accountId, appId: identity.appId }],
      },
      accountDetails: {
        accountId: identity.accountId,
        appId: null,
        creationTimeUnix: Math.floor(creationTime.getTime() / 1000),
        creationTime,
        associatedUserId: identity.userId,
        lastSessionStartTimeUnix: now,
        lastSessionStartTime: new Date(now),
        sessionIndex: 1,
      },
      playerData: {
        playerName: identity.playerName,
        pilotIcon: "",
        pilotBanner: "",
        isServerBot: false,
        equippedVehicleConfig: aircraftId,
        generalRankingPoints: 0,
        cachedTotalVehicleRankingPoints: 0,
        playerXp: 0,
        playerXpCountSinceReset: 0,
        lastPlayerXpAwardTimeMs: 0,
        lastPlayerXpAwardedTime: new Date(0),
        seenPlayerXp: 0,
        highestTotalVehicleRankingPoints: 0,
        seenHighestTotalVehicleRankingPoints: 0,
        cachedHangerPointsSum: 0,
        playerActivity: "local-dev",
        hasShownRegisterWorkflowAfterFirstPurchase: false,
        lastActiveMs: now,
        lastActiveUtc: new Date(now),
        migrationVersion: 0,
        friendCode: "LOCALDEV",
        completedMigrations: [],
        hasPlayedFakeBotMatch: false,
        hasExitedGameplayTutorialMatch: true,
        hasPlayedGameplayTutorialMatch: true,
        careerCounters: {
          overall: {
            pvpMatchesPlayed: 0,
            pvpMatchesWon: 0,
            pvpDeaths: 0,
            pvpScore: 0,
            pvpMvpCount: 0,
            pvpDamageToObjectives: 0,
            missionsPlays: 0,
            lossStreak: 0,
          },
          countersByGameModeCategory: null,
          countersByVehicleConfig: {
            [aircraftId]: {
              pvpMatchesPlayed: 0,
              pvpMatchesWon: 0,
              pvpDeaths: 0,
              pvpScore: 0,
              pvpMvpCount: 0,
              pvpDamageToObjectives: 0,
              missionsPlays: 0,
              lossStreak: 0,
            },
          },
        },
        legacyAlphaTrophies: 0,
        legacyAlphaRank: 0,
        isDirectChatSilenced: false,
        isAllChatSilenced: false,
      },
      equipment: {
        "family:f5e": {
          planeFamily: "family:f5e",
          planeLevel: 1,
          unixTimeLastFlown: now,
          equippedModsBySlotIndex: {},
          equippedActiveAbility: null,
          equippedPassiveAbility: null,
          modsAndAbilityLevels: null,
          trophyCount: 0,
          highestTrophyCount: 0,
          recentGamesWon: [],
          matchesPlayed: 0,
          matchesWon: 0,
        },
      },
    },
  };
}
